import { useContext, useEffect, useState } from 'react'
import { FontIcon, Stack, TextField } from '@fluentui/react'
import { SendRegular } from '@fluentui/react-icons'
import { useLocation, useNavigate } from 'react-router-dom'

import Send from '../../assets/Send.svg'

import styles from './QuestionInput.module.css'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'
import uuid from 'react-uuid'
import {
  ChatMessage,
  Conversation,
} from "../../api";

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

const base64PDFPreview = await fetch('https://i.imgur.com/gBi0yjr.png')
.then(response => response.blob())
.then(blob => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise((res) => {
    reader.onloadend = () => {
    res(reader.result);
  }})
})

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId: propConversationId }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const appStateContext = useContext(AppStateContext)
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false)
  const location = useLocation();
  const navigate = useNavigate();
  const [effectiveConversationId, setEffectiveConversationId] = useState<string | undefined>(propConversationId);

  // Check URL parameters for conversation_id and update state accordingly
  useEffect(() => {
    // Parse conversation_id from URL hash or search params
    const urlParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace('#/', '').split('?')[1] || '');
    const urlConversationId = urlParams.get('conversation_id') || hashParams.get('conversation_id');
    
    console.log("urlConversationId", urlConversationId);
    console.log("location.search", location.search);
    console.log("location.hash", location.hash);
    
    if (urlConversationId) {
      setEffectiveConversationId(urlConversationId);
      
      // If we have a conversation ID from URL and it's different from current chat, update current chat
      if (appStateContext?.state.currentChat?.id !== urlConversationId) {
        // First check if this conversation exists in history
        const existingConversation = appStateContext?.state.chatHistory?.find(
          conv => conv.id === urlConversationId
        );

        if (existingConversation) {
          appStateContext?.dispatch({ 
            type: 'UPDATE_CURRENT_CHAT', 
            payload: existingConversation 
          });
        } else {
          // Create new conversation with this ID
          const newConversation: Conversation = {
            id: urlConversationId,
            title: '',
            messages: [],
            date: new Date().toISOString()
          };
          appStateContext?.dispatch({ 
            type: 'UPDATE_CURRENT_CHAT', 
            payload: newConversation 
          });
        }
      }
    } else if (propConversationId) {
      setEffectiveConversationId(propConversationId);
      // Update URL with the prop conversation ID using hash navigation
      navigate(`/#/?conversation_id=${propConversationId}`, { replace: true });
    }
  }, [location, propConversationId, appStateContext?.state.chatHistory, navigate]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    appStateContext?.dispatch({ type: 'SET_IS_LOADING', payload: true})
    const file = event.target.files?.[0];
    if (!file) {
      console.error("No file selected.");
      return;
    }
  
    if (file.type === 'application/pdf') {
      const formData = new FormData();
      formData.append('pdf', file);
      console.log("Uploading PDF:", file.name, "for conversation:", effectiveConversationId);
      
      let currentConversationId = effectiveConversationId;
      if(currentConversationId === undefined){
        currentConversationId = uuid();
        setEffectiveConversationId(currentConversationId);
        // Update URL with new conversation ID using hash navigation
        navigate(`/#/?conversation_id=${currentConversationId}`, { replace: true });
      }

      let userMessage: ChatMessage = {
        id: uuid(),
        role: 'user',
        content: `Uploading ${file.name}...`,
        date: new Date().toISOString()
      }

      let conversation: Conversation = appStateContext?.state.currentChat || {
        id: currentConversationId,
        title: file.name,
        messages: [],
        date: new Date().toISOString()
      };
      
      conversation.messages.push(userMessage);
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });

      try {
        const response = await fetch('./doc_upload/' + currentConversationId, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Upload response:", result);

        let doneUploadingMessage: ChatMessage = {
          id: uuid(),
          role: 'user',
          content: `Successfully uploaded ${file.name}. You can now ask questions about this document.`,
          date: new Date().toISOString()
        }
        conversation.messages.push(doneUploadingMessage);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });

      } catch (error) {
        console.error("Upload error:", error);
        let errorMessage: ChatMessage = {
          id: uuid(),
          role: 'error',
          content: `Failed to upload ${file.name}. Please try again.`,
          date: new Date().toISOString()
        }
        conversation.messages.push(errorMessage);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
      } finally {
        appStateContext?.dispatch({ type: 'SET_IS_LOADING', payload: false});
      }
    } else if (file) {
      await convertToBase64(file);
    }
  };

  const convertToBase64 = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800);
      setBase64Image(resizedBase64);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage["content"] = base64Image ? 
      [{ type: "text", text: question }, { type: "image_url", image_url: { url: base64Image } }] : 
      question.toString();

    if (effectiveConversationId && questionTest !== undefined) {
      onSend(questionTest, effectiveConversationId)
      setBase64Image(null)
    } else {
      // Generate new conversation ID if none exists
      const newConversationId = uuid();
      setEffectiveConversationId(newConversationId);
      // Update URL with new conversation ID using hash navigation
      navigate(`/#/?conversation_id=${newConversationId}`, { replace: true });
      onSend(questionTest, newConversationId)
      setBase64Image(null)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <TextField
        className={styles.questionInputTextArea}
        placeholder={placeholder}
        multiline
        resizable={false}
        borderless
        value={question}
        onChange={onQuestionChange}
        onKeyDown={onEnterPress}
      />
      
      <div className={styles.fileInputContainer}>
        <input
          type="file"
          id="fileInput"
          onChange={(event) => handleUpload(event)}
          accept="image/*,.pdf"
          className={styles.fileInput}
        />
        <label htmlFor="fileInput" className={styles.fileLabel} aria-label='Upload Image or PDF'>
          <FontIcon
            className={styles.fileIcon}
            iconName={'BulkUpload'}
            aria-label='Upload Photo or PDF'
          />
        </label>
      </div>
      {/* TODO: Support multiple types of upload here. */}
      {base64Image && <img className={styles.uploadedImage} src={base64Image} alt="Uploaded Preview" />}
      <div
        className={styles.questionInputSendButtonContainer}
        role="button"
        tabIndex={0}
        aria-label="Ask question button"
        onClick={sendQuestion}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
        {sendQuestionDisabled ? (
          <SendRegular className={styles.questionInputSendButtonDisabled} />
        ) : (
          <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />
        )}
      </div>
      <div className={styles.questionInputBottomBorder} />
    </Stack>
  )
}
